import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  Bike,
  BikeList,
  type CreateBikeBody,
  type SoldBody,
  type UpdateBikeBody,
} from '@klunkers/shared'
import { api } from '@/api/http'
import { getPreference, removePreference, setPreference } from '@/native/preferences'
import { localStore } from '@/native/storage'

const CACHE_KEY = 'cache:bikes'
/** The bike filter (null = all bikes). A new key on purpose: builds before it
 *  remembered a "current bike" under 'klunkers.currentBike', and turning that
 *  into a filter on upgrade would hide things without the rider asking. */
const FILTER_KEY = 'klunkers.bikeFilter'
const LEGACY_CURRENT_KEY = 'klunkers.currentBike'

export type Milestone = 'third_bike' | null

/** The rider's bikes. Cached locally so the garage opens with no signal. */
export const useGarageStore = defineStore('garage', () => {
  const bikes = ref<Bike[]>([])
  const loaded = ref(false)
  const loading = ref(false)
  const offline = ref(false)
  /** The bike the switcher is set to, or null for all bikes. Remembered per device. */
  const currentBikeId = ref<string | null>(null)
  const filterLoaded = ref(false)

  /** Bikes still in use come first in pickers. */
  const activeBikes = computed(() =>
    bikes.value.filter((b) => b.status === 'active' || b.status === 'building'),
  )

  /**
   * The bike the garage is filtered to (Due, Inbox, Jobs), or null for all
   * bikes. A remembered bike that has since gone counts as all bikes.
   */
  const currentBike = computed<Bike | null>(
    () => bikes.value.find((b) => b.id === currentBikeId.value) ?? null,
  )

  /**
   * The bike forms default to (capture, new job, wishlist): the filtered bike,
   * otherwise the first active bike, otherwise any bike.
   */
  const defaultBike = computed<Bike | null>(
    () => currentBike.value ?? activeBikes.value[0] ?? bikes.value[0] ?? null,
  )

  const byId = (id: string) => bikes.value.find((b) => b.id === id) ?? null

  async function saveCache() {
    await localStore()
      .set(CACHE_KEY, bikes.value)
      .catch(() => {})
  }

  async function load() {
    loading.value = true
    try {
      if (!filterLoaded.value) {
        currentBikeId.value = await getPreference(FILTER_KEY)
        filterLoaded.value = true
        void removePreference(LEGACY_CURRENT_KEY)
      }
      const cached = await localStore()
        .get<Bike[]>(CACHE_KEY)
        .catch(() => undefined)
      if (cached && !loaded.value) {
        bikes.value = cached
        loaded.value = true
      }
      const { bikes: fresh } = await api('/bikes', { schema: BikeList })
      bikes.value = fresh
      offline.value = false
      await saveCache()
    } catch (err) {
      offline.value = true
      if (!loaded.value) throw err
    } finally {
      loaded.value = true
      loading.value = false
    }
  }

  /** Filter the garage to one bike, or to all bikes with null. */
  async function selectBike(id: string | null) {
    currentBikeId.value = id
    filterLoaded.value = true
    if (id === null) await removePreference(FILTER_KEY)
    else await setPreference(FILTER_KEY, id)
  }

  async function create(body: CreateBikeBody): Promise<{ bike: Bike; milestone: Milestone }> {
    const bike = await api('/bikes', { method: 'POST', body, schema: Bike })
    bikes.value = [...bikes.value, bike]
    await saveCache()
    // A bike you've just added is the one you're about to work on.
    await selectBike(bike.id)
    return { bike, milestone: bikes.value.length === 3 ? 'third_bike' : null }
  }

  async function update(id: string, body: UpdateBikeBody): Promise<Bike> {
    const bike = await api(`/bikes/${id}`, { method: 'PATCH', body, schema: Bike })
    bikes.value = bikes.value.map((b) => (b.id === id ? bike : b))
    await saveCache()
    return bike
  }

  /** Sold it: parts fitted go with it; history stays. */
  async function sold(id: string, body: SoldBody): Promise<Bike> {
    const bike = await api(`/bikes/${id}/sold`, { method: 'POST', body, schema: Bike })
    bikes.value = bikes.value.map((b) => (b.id === id ? bike : b))
    await saveCache()
    return bike
  }

  async function remove(id: string) {
    await api(`/bikes/${id}`, { method: 'DELETE' })
    bikes.value = bikes.value.filter((b) => b.id !== id)
    await saveCache()
    if (currentBikeId.value === id) await selectBike(null)
  }

  async function reset() {
    bikes.value = []
    loaded.value = false
    currentBikeId.value = null
    filterLoaded.value = false
    await removePreference(FILTER_KEY)
    await localStore()
      .delete(CACHE_KEY)
      .catch(() => {})
  }

  return {
    bikes,
    activeBikes,
    loaded,
    loading,
    offline,
    currentBikeId,
    currentBike,
    defaultBike,
    byId,
    load,
    selectBike,
    create,
    update,
    sold,
    remove,
    reset,
  }
})
